import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Empty,
  Icon,
  Input,
  Modal,
  Progress,
  Slider,
  Spin,
  Tooltip,
  message
} from 'antd'
import moment from 'moment'

import './TimelineEditorModal.scss'

const TimelineEditorModal = ({
  visible,
  projectId,
  loading,
  preparing,
  error,
  eligibilityError,
  statusText,
  progress,
  exportsLoading,
  exportsError,
  exports = [],
  historyUrls = [],
  onClose,
  onRefresh,
  onContinue,
  onAddToHistory
}) => {
  const [includeMusic, setIncludeMusic] = useState(false)
  const [userPrompt, setUserPrompt] = useState('')
  const [musicVolume, setMusicVolume] = useState(0.3)
  const [previewExport, setPreviewExport] = useState(null)
  const [addingUrl, setAddingUrl] = useState(null)
  const [addedUrls, setAddedUrls] = useState([])
  const [musicSetupVisible, setMusicSetupVisible] = useState(false)

  useEffect(() => {
    if (!visible) return

    setIncludeMusic(false)
    setUserPrompt('')
    setMusicVolume(0.3)
    setPreviewExport(null)
    setAddingUrl(null)
    setAddedUrls(historyUrls)
    setMusicSetupVisible(false)
  }, [historyUrls, projectId, visible])

  const handleContinue = () => {
    if (projectId) {
      onContinue({
        includeMusic: false,
        userPrompt: '',
        musicVolume
      })
      return
    }

    setMusicSetupVisible(true)
  }

  const handlePrepareTimeline = () => {
    setMusicSetupVisible(false)
    onContinue({
      includeMusic,
      userPrompt: userPrompt.trim(),
      musicVolume
    })
  }

  const handleAddToHistory = async timelineExport => {
    if (!onAddToHistory || !timelineExport?.url) return

    setAddingUrl(timelineExport.url)

    try {
      const added = await onAddToHistory(timelineExport)

      if (added === false) {
        message.info('This export is already in video history')
        return
      }

      setAddedUrls(currentUrls =>
        currentUrls.includes(timelineExport.url)
          ? currentUrls
          : [...currentUrls, timelineExport.url]
      )
      message.success('Timeline export added to video history')
    } catch (addError) {
      message.error(
        addError?.message || 'Failed to add the export to video history'
      )
    } finally {
      setAddingUrl(null)
    }
  }

  const footer = [
    projectId && !eligibilityError ? (
      <Button
        key="refresh"
        icon="reload"
        loading={exportsLoading}
        onClick={onRefresh}
      >
        Refresh exports
      </Button>
    ) : null,
    <Button key="close" onClick={onClose} disabled={preparing}>
      Close
    </Button>,
    !eligibilityError ? (
      <Button
        key="continue"
        type="primary"
        icon="export"
        loading={preparing}
        disabled={loading}
        onClick={handleContinue}
      >
        Open Editor
      </Button>
    ) : null
  ].filter(Boolean)

  return (
    <>
      <Modal
        centered
        visible={visible}
        title="Timeline Editor"
        width={960}
        className="timeline-editor-modal"
        maskClosable={false}
        closable={!preparing}
        keyboard={!preparing}
        destroyOnClose
        onCancel={() => {
          if (!preparing) {
            setMusicSetupVisible(false)
            onClose()
          }
        }}
        footer={footer}
      >
        {loading ? (
          <div className="timeline-editor-modal__state">
            <Spin size="large" />
            <span>{statusText || 'Loading timeline project...'}</span>
          </div>
        ) : eligibilityError ? (
          <div className="timeline-editor-modal__state timeline-editor-modal__state--eligibility">
            <Icon
              type="exclamation-circle"
              className="timeline-editor-modal__eligibility-icon"
            />
            <Alert
              type="warning"
              showIcon
              message="Timeline Editor is not ready"
              description={eligibilityError}
            />
          </div>
        ) : (
          <>
            {preparing && (
              <div className="timeline-editor-modal__progress">
                <div className="timeline-editor-modal__progress-copy">
                  <Spin size="small" />
                  <span>{statusText || 'Preparing timeline editor...'}</span>
                  <strong>{Math.max(0, Math.min(100, progress || 0))}%</strong>
                </div>
                <Progress
                  percent={Math.max(0, Math.min(100, progress || 0))}
                  showInfo={false}
                  strokeWidth={7}
                />
              </div>
            )}

            {error && (
              <Alert
                type="error"
                showIcon
                message="Timeline editor request failed"
                description={error}
                className="timeline-editor-modal__alert"
              />
            )}

            <section className="timeline-editor-exports">
              <div className="timeline-editor-exports__header">
                <div>
                  <strong>Exported versions</strong>
                  <span>
                    Preview rendered versions or add one to final-video history.
                  </span>
                </div>
                <span className="timeline-editor-exports__count">
                  {exports.length}
                </span>
              </div>

              {exportsLoading ? (
                <div className="timeline-editor-modal__state timeline-editor-modal__state--compact">
                  <Spin />
                  <span>Loading exported versions...</span>
                </div>
              ) : exportsError ? (
                <Alert
                  type="error"
                  showIcon
                  message="Could not load timeline exports"
                  description={exportsError}
                />
              ) : exports.length === 0 ? (
                <div className="timeline-editor-modal__state timeline-editor-modal__state--compact">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      projectId
                        ? 'No timeline exports yet'
                        : 'No exports yet. Continue to Timeline to prepare your editable project.'
                    }
                  />
                </div>
              ) : (
                <div className="timeline-exports-list">
                  {exports.map(item => {
                    const isAdded = addedUrls.includes(item.url)

                    return (
                      <article
                        key={`${item.version}-${item.created_at || item.url}`}
                        className="timeline-export-item"
                      >
                        <button
                          type="button"
                          className="timeline-export-item__preview-button"
                          onClick={() => setPreviewExport(item)}
                        >
                          <video
                            className="timeline-export-item__preview"
                            src={item.url}
                            preload="metadata"
                          />
                          <span className="timeline-export-item__play">
                            <Icon type="play-circle" />
                          </span>
                        </button>

                        <div className="timeline-export-item__body">
                          <div className="timeline-export-item__header">
                            <strong className="timeline-export-item__version">
                              Version {item.version}
                            </strong>
                            <div className="timeline-export-item__meta">
                              <span className="timeline-export-item__tag">
                                {String(item.format || 'video').toUpperCase()}
                              </span>
                              {Number.isFinite(Number(item.duration)) && (
                                <span className="timeline-export-item__tag">
                                  {Math.round(Number(item.duration))}s
                                </span>
                              )}
                              {Array.isArray(item.range) && (
                                <span className="timeline-export-item__tag">
                                  {item.range[0]}s - {item.range[1]}s
                                </span>
                              )}
                            </div>
                          </div>

                          <span className="timeline-export-item__date">
                            {item.created_at
                              ? moment(item.created_at).format(
                                  'MMM D, YYYY [at] h:mm A'
                                )
                              : 'Export date unavailable'}
                          </span>

                          <div className="timeline-export-item__actions">
                            <Button
                              icon="play-circle"
                              onClick={() => setPreviewExport(item)}
                            >
                              Preview
                            </Button>
                            {onAddToHistory && (
                              <Tooltip
                                title={
                                  isAdded
                                    ? 'Already added to video history'
                                    : 'Add this export without changing the selected final video'
                                }
                              >
                                <Button
                                  type="primary"
                                  icon={isAdded ? 'check' : 'plus'}
                                  disabled={isAdded || Boolean(addingUrl)}
                                  loading={addingUrl === item.url}
                                  onClick={() => handleAddToHistory(item)}
                                >
                                  {isAdded ? 'Added' : 'Add to history'}
                                </Button>
                              </Tooltip>
                            )}
                            <Button
                              icon="download"
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Download
                            </Button>
                          </div>
                        </div>
                      </article>
                    )
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </Modal>

      <Modal
        centered
        visible={musicSetupVisible}
        title="Prepare your timeline"
        width={520}
        className="timeline-music-modal"
        maskClosable={!preparing}
        closable={!preparing}
        keyboard={!preparing}
        destroyOnClose
        onCancel={() => {
          if (!preparing) setMusicSetupVisible(false)
        }}
        footer={[
          <Button
            key="cancel"
            disabled={preparing}
            onClick={() => setMusicSetupVisible(false)}
          >
            Cancel
          </Button>,
          <Button
            key="continue"
            type="primary"
            icon="arrow-right"
            loading={preparing}
            onClick={handlePrepareTimeline}
          >
            Prepare & Open Timeline
          </Button>
        ]}
      >
        <div className="timeline-music-modal__heading">
          <strong>Would you like background music?</strong>
          <span>
            Choose how the timeline should be prepared. You can still edit audio
            tracks later.
          </span>
        </div>

        <div
          className="timeline-music-modal__choices"
          role="radiogroup"
          aria-label="Background music preference"
        >
          <button
            type="button"
            role="radio"
            aria-checked={!includeMusic}
            className={`timeline-music-choice ${
              !includeMusic ? 'timeline-music-choice--selected' : ''
            }`}
            onClick={() => setIncludeMusic(false)}
          >
            <span className="timeline-music-choice__icon">
              <Icon type="sound" />
            </span>
            <span className="timeline-music-choice__copy">
              <strong>No background music</strong>
              <span>Keep the timeline focused on its existing audio.</span>
            </span>
            <span className="timeline-music-choice__check">
              <Icon type={!includeMusic ? 'check-circle' : 'right'} />
            </span>
          </button>

          <button
            type="button"
            role="radio"
            aria-checked={includeMusic}
            className={`timeline-music-choice ${
              includeMusic ? 'timeline-music-choice--selected' : ''
            }`}
            onClick={() => setIncludeMusic(true)}
          >
            <span className="timeline-music-choice__icon">
              <Icon type="customer-service" />
            </span>
            <span className="timeline-music-choice__copy">
              <strong>Generate background music</strong>
              <span>Create a soundtrack on a separate audio track.</span>
            </span>
            <span className="timeline-music-choice__check">
              <Icon type={includeMusic ? 'check-circle' : 'right'} />
            </span>
          </button>
        </div>

        {includeMusic && (
          <div className="timeline-music-modal__options">
            <label htmlFor="timeline-music-prompt">Music direction</label>
            <Input.TextArea
              id="timeline-music-prompt"
              value={userPrompt}
              onChange={event => setUserPrompt(event.target.value)}
              placeholder="For example: calm, modern, with a subtle build"
              autosize={{ minRows: 2, maxRows: 4 }}
              maxLength={240}
              disabled={preparing}
            />

            <div className="timeline-music-modal__volume-header">
              <label>Music volume</label>
              <span>{Math.round(musicVolume * 100)}%</span>
            </div>
            <Slider
              min={0}
              max={1}
              step={0.05}
              value={musicVolume}
              tipFormatter={value => `${Math.round(value * 100)}%`}
              onChange={setMusicVolume}
              disabled={preparing}
            />
          </div>
        )}
      </Modal>

      <Modal
        centered
        visible={!!previewExport}
        title={previewExport ? `Timeline Export v${previewExport.version}` : ''}
        width={920}
        footer={null}
        destroyOnClose
        onCancel={() => setPreviewExport(null)}
        className="timeline-export-preview-modal"
      >
        {previewExport && (
          <video
            key={previewExport.url}
            src={previewExport.url}
            controls
            autoPlay
          />
        )}
      </Modal>
    </>
  )
}

export default TimelineEditorModal
